import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { AppDispatch, RootState } from 'app/store';
import {
  IDiscountBrand,
  ICreateDiscountBrandPayload,
  IDeleteDiscountBrandPayload,
  IDiscountBrandListParams,
  IUpdateDiscountBrandPayload,
} from 'entities/Discount/Discount.models';
import { discountTransport } from 'entities/Discount/Discount.transport';

interface IState {
  discountBrand: IDiscountBrand | null;
  discountBrandList: IDiscountBrand[];
  isDiscountLoading: boolean;
}

const initialState: IState = {
  discountBrand: null,
  discountBrandList: [],
  isDiscountLoading: false,
};

export const discountSlice = createSlice({
  name: 'discount',
  initialState,
  reducers: {
    setDiscountBrand(state, action: PayloadAction<IDiscountBrand | null>) {
      state.discountBrand = action.payload;
    },
    setDiscountBrandList(state, action: PayloadAction<IDiscountBrand[]>) {
      state.discountBrandList = action.payload;
    },
    setIsDiscountLoading(state, action: PayloadAction<boolean>) {
      state.isDiscountLoading = action.payload;
    },
    addDiscountBrandListItem(state, action: PayloadAction<IDiscountBrand>) {
      state.discountBrandList.push(action.payload);
    },
    patchDiscountBrandListItem(state, action: PayloadAction<IDiscountBrand>) {
      const discountBrand = state.discountBrandList.find((item) => item.id === action.payload.id);

      if (!discountBrand) {
        return;
      }

      Object.assign(discountBrand, action.payload);
    },
    removeDiscountBrandListItem(state, action: PayloadAction<number>) {
      state.discountBrandList = state.discountBrandList.filter((item) => item.id !== action.payload);
    },
  },
});

export const {
  setDiscountBrand,
  setDiscountBrandList,
  setIsDiscountLoading,
  addDiscountBrandListItem,
  patchDiscountBrandListItem,
  removeDiscountBrandListItem,
} = discountSlice.actions;

export const selectDiscountBrand = (state: RootState) => state.discount.discountBrand;
export const selectDiscountBrandList = (state: RootState) => state.discount.discountBrandList;
export const selectIsDiscountLoading = (state: RootState) => state.discount.isDiscountLoading;

export const discountReducer = discountSlice.reducer;

export const getDiscountBrandList = (params: IDiscountBrandListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsDiscountLoading(true));

  try {
    const response = await discountTransport.get(params);
    dispatch(setDiscountBrandList(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsDiscountLoading(false));
  }
};

export const createDiscountBrand = (payload: ICreateDiscountBrandPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsDiscountLoading(true));

  try {
    const response = await discountTransport.create(payload);
    dispatch(addDiscountBrandListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsDiscountLoading(false));
  }
};

export const updateDiscountBrand = (payload: IUpdateDiscountBrandPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsDiscountLoading(true));

  try {
    const response = await discountTransport.update(payload);
    dispatch(patchDiscountBrandListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsDiscountLoading(false));
  }
};

export const deleteDiscountBrand = (payload: IDeleteDiscountBrandPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsDiscountLoading(true));

  try {
    const response = await discountTransport.delete(payload);
    dispatch(removeDiscountBrandListItem(response.id));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsDiscountLoading(false));
  }
};
